<script lang="ts" setup>
import { computed, ref, type Ref, toRefs } from 'vue';
import { Event, type EventMetadata } from '@/models/Event';
import { apiUrl, globalParam } from '@/helpers/globals';
import { instance as api } from '@/helpers/api';
import dayjs from 'dayjs';

const props = defineProps<{
    record: Event;
}>();
const { record } = toRefs(props);

const additionalMetadata: Ref<EventMetadata | null> = ref(null);

const legalInfo = computed(() => {
    if (additionalMetadata.value?.legalAcceptances?.length) {
        return additionalMetadata.value.legalAcceptances.map((entry) => {
            return {
                user: entry.user,
                acceptanceType: entry.acceptanceType,
                date: dayjs(entry.timestamp),
            };
        });
    } else {
        return null;
    }
});

const hideFrontendLink = computed(() => {
    return globalParam('event.hide_frontend_links');
});

const loadAdditionalMetadata = async () => {
    try {
        const ret = await api().get('/Event/loadMetadata', {
            params: { id: record.value.id },
        });
        if (ret?.data) {
            additionalMetadata.value = ret.data;
        }
    } catch (e) {
        // pass
    }
};

loadAdditionalMetadata();
</script>

<template>
    <v-container fluid class="pa-5 overflow-auto">
        <h4>{{ $t('metadata_for', { title: record.title }) }}</h4>
        <table class="stripe-table mt-2 tgk-paragraph-text">
            <tbody>
                <tr>
                    <td>
                        <b>{{ $t('rec_id') }}</b>
                    </td>
                    <td>
                        <span>{{ record.id }}</span> / UUID: <span>{{ record.uuid }}</span>
                    </td>
                </tr>
                <tr>
                    <td>
                        <b>{{ $t('created') }}</b>
                    </td>
                    <td>
                        {{ record.created.formatIf('dd, DD.MM.YYYY HH:mm') }}
                    </td>
                </tr>
                <tr>
                    <td>
                        <b>{{ $t('creator') }}</b>
                    </td>
                    <td>
                        {{ record.creator }}
                    </td>
                </tr>
                <tr>
                    <td>
                        <b>{{ $t('modified') }}</b>
                    </td>
                    <td>
                        {{ record.modified.formatIf('dd, DD.MM.YYYY HH:mm') }}
                        <span v-if="record.modifier"> / {{ record.modifier }}</span>
                    </td>
                </tr>
                <tr v-if="legalInfo">
                    <td>Legal</td>
                    <td>
                        <dl v-for="info in legalInfo" :key="info.acceptanceType" class="legal-entry">
                            <dt>
                                <KpIcon icon-cls="tick" color="grey" left size="16" /> {{ $t(info.acceptanceType) }}
                            </dt>
                            <dd>{{ info.user }}, {{ info.date.formatIf('DD.MM.YYYY HH:MM') }}</dd>
                        </dl>
                    </td>
                </tr>
                <tr>
                    <td>
                        <b>{{ $t('hauptsparte') }}</b>
                    </td>
                    <td>
                        {{ record.main_genre_group }}
                    </td>
                </tr>
                <tr>
                    <td>
                        <b>{{ $t('weitere_sparten') }}</b>
                    </td>
                    <td>
                        {{ record.event_genre_groups }}
                    </td>
                </tr>
                <tr>
                    <td>
                        <b>{{ $t('import_protection') }}</b>
                    </td>
                    <td>
                        {{ record.import_protection ? $t('yes') : $t('no') }}
                    </td>
                </tr>
                <tr>
                    <td><b>Business Partner's Original ID</b></td>
                    <td>
                        {{ record.bp_import_id }}
                    </td>
                </tr>
                <tr>
                    <td><b>Business Partner Identification</b></td>
                    <td>
                        {{ record.bp_import_origin }}
                    </td>
                </tr>
                <tr>
                    <td><b>Import Date</b></td>
                    <td>
                        {{ record.bp_import_date.formatIf('dd, DD.MM.YYYY HH:mm') }}
                    </td>
                </tr>
                <tr>
                    <td><b>GUID</b></td>
                    <td>
                        {{ record.uuid || '-' }}
                    </td>
                </tr>
                <tr v-if="!hideFrontendLink">
                    <td>
                        <b>{{ $t('link') }}</b>
                    </td>
                    <td>
                        <a :href="record.detailUrl" target="_blank">{{ record.detailUrl }}</a>
                    </td>
                </tr>
                <tr v-if="!hideFrontendLink">
                    <td>
                        <b>{{ $t('qr_code') }}</b>
                    </td>
                    <td>
                        <a
                            :href="apiUrl(`/Event/linkQrCode?id=${record.id}&type=png&force_download=true`)"
                            target="_blank"
                        >
                            <img
                                :src="apiUrl(`/Event/linkQrCode?id=${record.id}&type=svg`)"
                                width="150"
                                height="150"
                                alt="QR Code"
                            />
                        </a>
                    </td>
                </tr>
            </tbody>
        </table>
    </v-container>
</template>

<style scoped lang="scss">
.legal-entry {
    display: flex;
    gap: 5px;
    dt {
        flex-basis: 150px;
    }
    dd {
        flex: 1;
    }
}

table td {
    border-collapse: collapse;
    border-style: solid;
    border-width: 1px;
    border-color: rgba(224, 224, 224, 1);
}
</style>
